import { LawThemes, TreeWithId } from '@livv/models';
import { getBookLink } from '@livv/utils/helpers';
import { Box, Link, SxProps, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { FC } from 'react';
import { treeThemeColor } from '@styles/theme';
import { sxs } from '@utils/mui/sx';
import { convertTimestamps } from '@utils/serverSide/utils';

const getColor = (theme: string) =>
    treeThemeColor[theme as keyof typeof treeThemeColor] || treeThemeColor[LawThemes.OTHER];
const CARD_TRANSITION = 'all 0.5s ease';

interface ThemeColorProps {
    theme: string;
}

const cardSx: SxProps = {
    '&:hover': {
        '.content': {
            maxHeight: 480,
            transform: 'rotateY(180deg)',
            transition: CARD_TRANSITION,
            transitionDelay: '0.25s',
        },
        boxShadow: 'shadows.2',
        maxHeight: 480,
        transform: 'translate(0, -8px)',
        transition: CARD_TRANSITION,
    },
    border: 1,
    borderColor: 'grey.600',
    borderRadius: 0.75,
    color: 'inherit',
    maxHeight: 280,
    overflow: 'hidden',
    textDecoration: 'none',
    transition: CARD_TRANSITION,
};

const themeSx = ({ theme }: ThemeColorProps) => ({
    bgcolor: getColor(theme),
    color: 'common.white',
    fontSize: 14,
    fontWeight: 'medium',
    p: 0.5,
    textAlign: 'center',
    textTransform: 'uppercase',
    width: '100%',
});

const faceSx: Record<'base' | 'front' | 'back', SxProps> = {
    back: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        p: 2,
        transform: 'rotateY(180deg)',
    },
    base: {
        MozBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        bgcolor: 'common.white',
        left: 0,
        minHeight: 290,
        textAlign: 'center',
        top: 0,
        width: '100%',
    },
    front: {
        pb: 2,
        position: 'absolute',
        pt: 5,
        px: 2,
    },
};

const innerCardSx: SxProps = {
    position: 'relative',
    transformStyle: 'preserve-3d',
    width: '100%',
};

interface BookItemProps {
    tree: TreeWithId;
}

const BookCard: FC<BookItemProps> = ({ tree }) => {
    const { t } = useTranslation('books');
    // To be able to call methods on Timestamp after been serialized by `JSON.parse(JSON.stringify())`
    // in getBookServerSideProps, need to re-initialize the Timestamp class object
    const bookWithCorrectDateFormat = convertTimestamps(tree);
    const { id: treeId, summary, theme, title } = bookWithCorrectDateFormat;

    return (
        <Link
            data-test-id={`book-link-${treeId}`}
            href={getBookLink({ bookId: tree.id })}
            sx={cardSx}
        >
            <Typography sx={themeSx({ theme })}>{t(`theme.${theme}`)}</Typography>

            <Box className="content" sx={innerCardSx}>
                <Box sx={sxs([faceSx.base, faceSx.front])}>
                    <Typography component="h3" sx={{ mt: 8, typography: 'h4' }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={sxs([faceSx.base, faceSx.back])}>
                    <Typography variant="body2">{summary}</Typography>
                </Box>
            </Box>
        </Link>
    );
};

export default BookCard;
